/**
 * The grown-up screen: progress over time, the words that keep tripping a
 * player up, and the keys to practise.
 *
 * Behind a maths question, so a young player wandering the menus does not land
 * here by accident. The question is a speed bump, not security.
 *
 * Everything shown is worked out in src/systems/parentStats.ts; this scene only
 * lays it out. Charts follow the data-viz method: one series each, named by its
 * title rather than a legend, recessive grid and axes, and only the latest value
 * labelled rather than a number on every point. They are read with the keyboard
 * on a canvas, so the hover layer an HTML chart would carry is replaced by those
 * direct labels and by the summary tiles above, which give the same numbers as
 * text.
 */
import Phaser from 'phaser';
import { drawVerticalGradient, MENU_GRADIENT } from '../art/backdrop';
import { devGateOpen, setDevState } from '../systems/devQuery';
import {
  HEAT_TIERS,
  heatTier,
  isGateAnswer,
  KEY_IDLE,
  KEYBOARD_ROWS,
  keyCounts,
  makeGateQuestion,
  niceTicks,
  PANEL_SURFACE,
  recentSeries,
  SERIES_COLOR,
  summarise,
  SUMMARY_WINDOW,
  topMissedWords,
  type ChartField,
  type GateQuestion,
} from '../systems/parentStats';
import type { ProfileSlot, SaveManager } from '../systems/SaveManager';
import { FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT, PALETTE } from '../types';
import { setTouchTyping } from '../systems/touchKeyboard';
import { IconButton } from './ui/IconButton';

/* Chart chrome for a dark surface, from the reference palette's ink roles. */
const INK_PRIMARY = PALETTE.snow;
const INK_SECONDARY = '#C3CCDA';
const INK_MUTED = '#8A97B0';
const INK_DARK = PALETTE.ink;
const GRID = 0x26344f;
const BASELINE = 0x3a4a68;
/** Reserved for "went up", always paired with words so it never carries meaning alone. */
const SUCCESS = '#0ca30c';

const MARGIN = 40;
const GAP = 40;
const COLUMN = (GAME_WIDTH - MARGIN * 2 - GAP) / 2;

const MAX_ANSWER_DIGITS = 4;

export class ParentStats extends Phaser.Scene {
  private save!: SaveManager;
  private view: Phaser.GameObjects.Container | undefined;
  private phase: 'gate' | 'stats' = 'gate';
  private question: GateQuestion = { prompt: '', answer: 0 };
  private typed = '';
  private slots: number[] = [];
  private slotIndex = 0;

  private answerText: Phaser.GameObjects.Text | undefined;
  private feedbackText: Phaser.GameObjects.Text | undefined;

  constructor() {
    super('ParentStats');
  }

  create(): void {
    setDevState({ scene: 'ParentStats' });
    // Scene instances are reused, so nothing may carry over from a last visit.
    this.view = undefined;
    this.typed = '';
    this.answerText = undefined;
    this.feedbackText = undefined;

    this.save = this.registry.get('save') as SaveManager;
    this.cameras.main.setBackgroundColor(PALETTE.sky);
    drawVerticalGradient(this, MENU_GRADIENT);

    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => this.onKey(event));
    // A back button on every screen size: Esc alone left a mouse with no way out.
    new IconButton(
      this,
      56,
      44,
      'back',
      () => this.scene.start('Title'),
      IS_PORTRAIT ? 56 : 52,
    ).setDepth(10);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => setTouchTyping(false));

    if (import.meta.env.DEV && devGateOpen()) this.showStats();
    else this.showGate();
  }

  /* -------------------------------------------------------------- input */

  private onKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.scene.start('Title');
      return;
    }
    if (this.phase === 'gate') {
      this.onGateKey(event);
      return;
    }
    if (event.key === 'ArrowLeft') this.switchProfile(-1);
    else if (event.key === 'ArrowRight') this.switchProfile(1);
  }

  private onGateKey(event: KeyboardEvent): void {
    if (/^[0-9]$/.test(event.key)) {
      if (this.typed.length < MAX_ANSWER_DIGITS) this.typed += event.key;
    } else if (event.key === 'Backspace') {
      this.typed = this.typed.slice(0, -1);
    } else if (event.key === 'Enter') {
      this.checkAnswer();
      return;
    } else {
      return;
    }
    this.answerText?.setText(this.typed === '' ? ' ' : this.typed);
  }

  private checkAnswer(): void {
    if (isGateAnswer(this.typed, this.question)) {
      this.showStats();
      return;
    }
    // A fresh question rather than another go at the same one: guessing
    // through every number is then no shortcut.
    this.question = makeGateQuestion(Math.random);
    this.typed = '';
    this.showGate('Not quite. Here is a different one.');
  }

  /* --------------------------------------------------------------- gate */

  private showGate(feedback = ''): void {
    this.phase = 'gate';
    // The answer is typed, so a phone needs its keyboard here.
    setTouchTyping(true);
    if (this.question.prompt === '' || feedback === '') {
      this.question = makeGateQuestion(Math.random);
    }
    const view = this.freshView();

    view.add(this.heading('For grown-ups', 150));
    view.add(
      this.label(
        GAME_WIDTH / 2,
        230,
        'Answer the sum to see progress and practice tips.',
        22,
        INK_SECONDARY,
      ).setOrigin(0.5),
    );
    view.add(this.label(GAME_WIDTH / 2, 320, this.question.prompt, 46, INK_PRIMARY).setOrigin(0.5));

    const box = this.add.graphics();
    box.fillStyle(PANEL_SURFACE, 1);
    box.fillRoundedRect(GAME_WIDTH / 2 - 120, 380, 240, 76, 16);
    box.lineStyle(3, 0x8ecae6, 1);
    box.strokeRoundedRect(GAME_WIDTH / 2 - 120, 380, 240, 76, 16);
    view.add(box);

    this.answerText = this.label(GAME_WIDTH / 2, 418, ' ', 44, PALETTE.gold).setOrigin(0.5);
    view.add(this.answerText);

    this.feedbackText = this.label(GAME_WIDTH / 2, 498, feedback, 22, PALETTE.sunset).setOrigin(
      0.5,
    );
    view.add(this.feedbackText);

    view.add(
      this.label(
        GAME_WIDTH / 2,
        // Tall stage: under the box, above where the device keyboard opens.
        IS_PORTRAIT ? 580 : GAME_HEIGHT - 40,
        IS_PORTRAIT
          ? 'Tap the box, type the answer, then Go.'
          : 'Type the answer, then Enter.   Esc goes back.',
        19,
        INK_MUTED,
      ).setOrigin(0.5),
    );
  }

  /* -------------------------------------------------------------- stats */

  private showStats(): void {
    this.phase = 'stats';
    setTouchTyping(false);
    const profiles = this.save.profiles();
    this.slots = profiles
      .map((profile, slot) => (profile === null ? -1 : slot))
      .filter((slot) => slot >= 0);

    const active = this.save.activeSlot();
    const start = active === null ? -1 : this.slots.indexOf(active);
    this.slotIndex = start >= 0 ? start : 0;
    this.drawStats();
  }

  private switchProfile(step: number): void {
    if (this.slots.length < 2) return;
    this.slotIndex = (this.slotIndex + step + this.slots.length) % this.slots.length;
    this.drawStats();
  }

  private drawStats(): void {
    const view = this.freshView();
    view.add(this.heading('Grown-up stats', 44));

    const slot = this.slots[this.slotIndex];
    const profile = slot === undefined ? null : (this.save.profiles()[slot] ?? null);
    if (profile === null) {
      view.add(
        this.label(
          GAME_WIDTH / 2,
          GAME_HEIGHT / 2,
          'No players yet. Make one from the Players screen.',
          26,
          INK_SECONDARY,
        ).setOrigin(0.5),
      );
      view.add(this.footer('Esc goes back'));
      return;
    }

    this.drawTabs(view);
    this.drawTiles(view, profile);

    if (IS_PORTRAIT) {
      // Tall stage: everything in one column, full width, top to bottom.
      const full = GAME_WIDTH - MARGIN * 2;
      const gap = 18;
      const keyboardH = 236;
      const start = 336;
      const room = GAME_HEIGHT - start - 52 - keyboardH - gap * 3;
      const chartH = Math.round(room * 0.36);
      const missedH = room - chartH * 2;
      let y = start;
      this.drawLineChart(view, MARGIN, y, full, chartH, profile, 'wpm');
      y += chartH + gap;
      this.drawLineChart(view, MARGIN, y, full, chartH, profile, 'accuracy');
      y += chartH + gap;
      this.drawMissedWords(view, MARGIN, y, full, missedH, profile);
      y += missedH + gap;
      this.drawKeyboard(view, MARGIN, y, full, keyboardH, profile);
      view.add(this.footer(this.slots.length > 1 ? 'Tap a name to switch player.' : ''));
      return;
    }

    const chartY = 236;
    const chartH = 206;
    this.drawLineChart(view, MARGIN, chartY, COLUMN, chartH, profile, 'wpm');
    this.drawLineChart(view, MARGIN + COLUMN + GAP, chartY, COLUMN, chartH, profile, 'accuracy');

    const lowerY = chartY + chartH + 18;
    const lowerH = GAME_HEIGHT - lowerY - 42;
    this.drawMissedWords(view, MARGIN, lowerY, COLUMN, lowerH, profile);
    this.drawKeyboard(view, MARGIN + COLUMN + GAP, lowerY, COLUMN, lowerH, profile);

    const hint =
      this.slots.length > 1 ? 'Left and right switch player.   Esc goes back.' : 'Esc goes back.';
    view.add(this.footer(hint));
  }

  private drawTabs(view: Phaser.GameObjects.Container): void {
    const profiles = this.save.profiles();
    const names = this.slots.map((slot) => profiles[slot]?.name ?? '');
    const spacing = 220;
    const left = GAME_WIDTH / 2 - ((names.length - 1) * spacing) / 2;
    names.forEach((name, i) => {
      const selected = i === this.slotIndex;
      const tab = this.label(
        left + i * spacing,
        96,
        name,
        selected ? 26 : 22,
        selected ? PALETTE.gold : INK_MUTED,
      ).setOrigin(0.5);
      // Names are tabs: a tap (or click) switches to that player.
      tab.setInteractive({ useHandCursor: true }).on('pointerup', () => {
        if (i === this.slotIndex) return;
        this.slotIndex = i;
        this.drawStats();
      });
      view.add(tab);
      if (selected) {
        const bar = this.add.graphics();
        bar.fillStyle(0xffc145, 1);
        bar.fillRoundedRect(left + i * spacing - 30, 114, 60, 4, 2);
        view.add(bar);
      }
    });
  }

  private drawTiles(view: Phaser.GameObjects.Container, profile: ProfileSlot): void {
    const s = summarise(profile);
    const tiles: Array<{ label: string; value: string; note?: string; noteColor?: string }> = [
      { label: 'Runs played', value: String(s.runs) },
      { label: 'Levels done', value: String(s.levelsCompleted) },
      { label: 'Stars', value: String(s.stars) },
      { label: 'Time played', value: formatMinutes(s.minutesPlayed) },
      {
        label: `Speed, last ${SUMMARY_WINDOW}`,
        value: s.runs === 0 ? '-' : `${s.recentWpm} wpm`,
        ...changeNote(s.wpmChange),
      },
      {
        label: `Accuracy, last ${SUMMARY_WINDOW}`,
        value: s.runs === 0 ? '-' : `${s.recentAccuracy}%`,
      },
    ];

    // Wide stage: one row of six. Tall stage: two rows of three.
    const perRow = IS_PORTRAIT ? 3 : tiles.length;
    const gap = 12;
    const width = (GAME_WIDTH - MARGIN * 2 - gap * (perRow - 1)) / perRow;
    const height = 84;
    tiles.forEach((tile, i) => {
      const x = MARGIN + (i % perRow) * (width + gap);
      const y = 132 + Math.floor(i / perRow) * (height + gap);
      const card = this.add.graphics();
      card.fillStyle(PANEL_SURFACE, 1);
      card.fillRoundedRect(x, y, width, height, 10);
      view.add(card);
      view.add(this.label(x + 14, y + 12, tile.label, 15, INK_MUTED));
      view.add(this.label(x + 14, y + 34, tile.value, 26, INK_PRIMARY));
      if (tile.note !== undefined) {
        view.add(this.label(x + 14, y + 64, tile.note, 13, tile.noteColor ?? INK_MUTED));
      }
    });
  }

  /**
   * One measure over the recent runs.
   *
   * A single series on its own axis: speed and accuracy are different scales,
   * so they get two charts rather than one chart with two y axes.
   */
  private drawLineChart(
    view: Phaser.GameObjects.Container,
    x: number,
    y: number,
    w: number,
    h: number,
    profile: ProfileSlot,
    field: ChartField,
  ): void {
    const title =
      field === 'wpm' ? 'Typing speed (words per minute)' : 'Accuracy (% of keys right)';
    const values = recentSeries(profile.history, field);

    const panel = this.add.graphics();
    panel.fillStyle(PANEL_SURFACE, 1);
    panel.fillRoundedRect(x, y, w, h, 12);
    view.add(panel);
    view.add(this.label(x + 16, y + 12, title, 18, INK_PRIMARY));

    if (values.length === 0) {
      view.add(
        this.label(
          x + w / 2,
          y + h / 2 + 10,
          'Play a level to start this chart.',
          18,
          INK_MUTED,
        ).setOrigin(0.5),
      );
      return;
    }

    // Accuracy lives near the top of its range, so a 0-100 axis would flatten
    // every change; a line chart need not start at zero, unlike a bar. Speed
    // keeps its zero, so a slow start is not drawn as a cliff.
    const low = field === 'accuracy' ? Math.max(0, Math.min(...values) - 3) : 0;
    const high = field === 'accuracy' ? 100 : Math.max(...values);
    const ticks = niceTicks(low, high);
    const min = Math.max(0, ticks[0] ?? 0);
    const max = Math.min(field === 'accuracy' ? 100 : Infinity, ticks[ticks.length - 1] ?? 10);

    const plot = { left: x + 52, right: x + w - 22, top: y + 46, bottom: y + h - 32 };
    const toY = (value: number): number =>
      plot.bottom - ((value - min) / Math.max(1e-6, max - min)) * (plot.bottom - plot.top);
    const toX = (i: number): number =>
      values.length === 1
        ? (plot.left + plot.right) / 2
        : plot.left + (i / (values.length - 1)) * (plot.right - plot.left);

    const grid = this.add.graphics();
    ticks
      .filter((value) => value >= min && value <= max)
      .forEach((value, t) => {
        const gy = toY(value);
        grid.lineStyle(1, t === 0 ? BASELINE : GRID, 1);
        grid.lineBetween(plot.left, gy, plot.right, gy);
        view.add(
          this.label(plot.left - 10, gy, formatTick(value), 13, INK_MUTED).setOrigin(1, 0.5),
        );
      });
    view.add(grid);

    view.add(this.label(plot.left, plot.bottom + 8, 'Earlier', 13, INK_MUTED));
    view.add(this.label(plot.right, plot.bottom + 8, 'Latest', 13, INK_MUTED).setOrigin(1, 0));

    const line = this.add.graphics();
    if (values.length > 1) {
      line.lineStyle(2, SERIES_COLOR, 1);
      line.beginPath();
      values.forEach((value, i) => {
        if (i === 0) line.moveTo(toX(i), toY(value));
        else line.lineTo(toX(i), toY(value));
      });
      line.strokePath();
    }

    // Only the latest point gets a marker and a number: a value on every point
    // is noise, and the latest is the one a parent is asking about.
    const last = values.length - 1;
    const lastValue = values[last] ?? 0;
    const lx = toX(last);
    const ly = toY(lastValue);
    line.fillStyle(PANEL_SURFACE, 1);
    line.fillCircle(lx, ly, 6);
    line.fillStyle(SERIES_COLOR, 1);
    line.fillCircle(lx, ly, 4);
    view.add(line);

    const suffix = field === 'wpm' ? ' wpm' : '%';
    view.add(
      this.label(
        lx - 10,
        ly - 12,
        `${formatTick(lastValue, 1)}${suffix}`,
        15,
        INK_PRIMARY,
      ).setOrigin(1, 1),
    );
  }

  private drawMissedWords(
    view: Phaser.GameObjects.Container,
    x: number,
    y: number,
    w: number,
    h: number,
    profile: ProfileSlot,
  ): void {
    const panel = this.add.graphics();
    panel.fillStyle(PANEL_SURFACE, 1);
    panel.fillRoundedRect(x, y, w, h, 12);
    view.add(panel);
    view.add(this.label(x + 16, y + 12, 'Most-missed words', 18, INK_PRIMARY));

    const words = topMissedWords(profile.missedWords);
    if (words.length === 0) {
      view.add(
        this.label(x + w / 2, y + h / 2 + 10, 'No missed words yet.', 18, INK_MUTED).setOrigin(0.5),
      );
      return;
    }

    // Four short columns rather than two long ones: twenty rows of 16px text
    // in two columns would not fit the panel without the lines touching.
    const columns = 4;
    const perColumn = 5;
    const rowHeight = Math.min(30, (h - 56) / perColumn);
    const columnWidth = (w - 40) / columns;
    words.forEach((entry, i) => {
      const column = Math.floor(i / perColumn);
      const row = i % perColumn;
      const cx = x + 20 + column * columnWidth;
      const cy = y + 48 + row * rowHeight;
      view.add(this.label(cx + 4, cy, entry.word, 17, INK_PRIMARY));
      view.add(
        this.label(cx + columnWidth - 14, cy + 1, `×${entry.count}`, 15, INK_MUTED).setOrigin(1, 0),
      );
    });
  }

  private drawKeyboard(
    view: Phaser.GameObjects.Container,
    x: number,
    y: number,
    w: number,
    h: number,
    profile: ProfileSlot,
  ): void {
    const panel = this.add.graphics();
    panel.fillStyle(PANEL_SURFACE, 1);
    panel.fillRoundedRect(x, y, w, h, 12);
    view.add(panel);
    view.add(this.label(x + 16, y + 12, 'Keys to practise', 18, INK_PRIMARY));

    const counts = keyCounts(profile.missedKeys);
    const max = Object.values(counts).reduce((best, n) => Math.max(best, n), 0);

    const key = 40;
    const gap = 6;
    const pitch = key + gap;
    const rowWidth = 10 * pitch - gap;
    const left = x + (w - rowWidth) / 2;
    const top = y + 44;

    const caps = this.add.graphics();
    view.add(caps);
    KEYBOARD_ROWS.forEach((row, r) => {
      [...row.keys].forEach((letter, c) => {
        const kx = left + row.indent * pitch + c * pitch;
        const ky = top + r * pitch;
        const tier = heatTier(counts[letter] ?? 0, max);
        caps.fillStyle(tier < 0 ? KEY_IDLE : (HEAT_TIERS[tier] ?? KEY_IDLE), 1);
        caps.fillRoundedRect(kx, ky, key, key, 6);
        // The two brightest tiers need dark ink to stay readable.
        const ink = tier >= HEAT_TIERS.length - 2 ? INK_DARK : INK_PRIMARY;
        view.add(
          this.label(kx + key / 2, ky + key / 2, letter.toUpperCase(), 17, ink).setOrigin(0.5),
        );
      });
    });

    // Legend, then the same information as text, so the heatmap is never the
    // only way to read it.
    const legendY = top + 3 * pitch + 6;
    const swatch = 22;
    const legendLeft = x + 24;
    view.add(this.label(legendLeft, legendY, 'Fewer', 13, INK_MUTED));
    const legend = this.add.graphics();
    HEAT_TIERS.forEach((color, i) => {
      legend.fillStyle(color, 1);
      legend.fillRoundedRect(legendLeft + 46 + i * (swatch + 4), legendY + 1, swatch, 13, 3);
    });
    view.add(legend);
    view.add(
      this.label(
        legendLeft + 46 + HEAT_TIERS.length * (swatch + 4) + 4,
        legendY,
        'More misses',
        13,
        INK_MUTED,
      ),
    );

    const worst = Object.entries(counts)
      .sort(([a, ca], [b, cb]) => cb - ca || a.localeCompare(b))
      .slice(0, 3)
      .map(([letter, n]) => `${letter.toUpperCase()} (${n})`);
    view.add(
      this.label(
        x + w - 24,
        legendY,
        worst.length === 0 ? 'No missed keys yet' : `Most missed: ${worst.join(', ')}`,
        13,
        INK_SECONDARY,
      ).setOrigin(1, 0),
    );
  }

  /* ------------------------------------------------------------ helpers */

  /** Replace whatever was on screen; returns the container to build into. */
  private freshView(): Phaser.GameObjects.Container {
    this.view?.destroy(true);
    this.view = this.add.container(0, 0);
    return this.view;
  }

  private heading(text: string, y: number): Phaser.GameObjects.Text {
    return this.add
      .text(GAME_WIDTH / 2, y, text, {
        fontFamily: FONT_FAMILY,
        fontSize: '44px',
        color: PALETTE.snow,
        stroke: PALETTE.ink,
        strokeThickness: 7,
      })
      .setOrigin(0.5);
  }

  private footer(text: string): Phaser.GameObjects.Text {
    return this.label(GAME_WIDTH / 2, GAME_HEIGHT - 22, text, 17, INK_MUTED).setOrigin(0.5);
  }

  private label(
    x: number,
    y: number,
    text: string,
    size: number,
    color: string,
  ): Phaser.GameObjects.Text {
    return this.add.text(x, y, text, { fontFamily: FONT_FAMILY, fontSize: `${size}px`, color });
  }
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${String(minutes % 60).padStart(2, '0')}m`;
}

function formatTick(value: number, decimals = 0): string {
  const rounded = Math.round(value * 10 ** decimals) / 10 ** decimals;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(decimals);
}

/**
 * The trend line under the speed tile.
 *
 * Up is green and says "up"; down stays muted and still says "down", because a
 * parent screen that paints a child's dip red is not helping anyone practise.
 */
function changeNote(change: number | null): { note?: string; noteColor?: string } {
  const window = SUMMARY_WINDOW;
  if (change === null) return { note: `Needs ${window * 2} runs to show a trend` };
  if (change > 0) return { note: `Up ${change} on the ${window} before`, noteColor: SUCCESS };
  if (change < 0) return { note: `Down ${Math.abs(change)} on the ${window} before` };
  return { note: `Steady on the ${window} before` };
}
